import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh,
  Download,
  Delete,
  PlayArrow,
  Pause,
  Close,
  Fullscreen,
  FullscreenExit,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_ENDPOINTS } from '../config/api';
import MemoryMonitor from '../components/MemoryMonitor';

interface Device {
  id: string;
  name: string;
  ip: string;
  status: 'online' | 'offline';
}

interface ScreenStatus {
  isConnected: boolean;
  isMonitoring: boolean;
  lastScreenshot?: string;
  screenshotCount: number;
  error?: string;
  memoryUsage?: number;
}

const DeviceMonitor: React.FC = () => {
  const { t } = useTranslation();
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const [refreshInterval, setRefreshInterval] = useState(500); // 0.5秒刷新，更实时
  const [isPaused, setIsPaused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [status, setStatus] = useState<ScreenStatus>({
    isConnected: false,
    isMonitoring: false,
    screenshotCount: 0
  });
  const [intervalRef, setIntervalRef] = useState<NodeJS.Timeout | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  // 从URL参数获取设备ID
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const deviceId = urlParams.get('device');
    if (deviceId) {
      setSelectedDeviceId(deviceId);
    }
  }, []);

  // 获取设备列表
  const { data: devices, isLoading: devicesLoading } = useQuery<Device[]>({
    queryKey: ['devices'],
    queryFn: async () => {
      const response = await axios.get(API_ENDPOINTS.DEVICES);
      return response.data;
    },
    refetchInterval: 5000
  });

  const selectedDevice = devices?.find(d => d.id === selectedDeviceId);

  // 检查设备连接状态
  const checkConnection = async () => {
    if (!selectedDeviceId) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.DEVICES}/${selectedDeviceId}/connection`);
      const data = await response.json();
      setStatus(prev => ({
        ...prev,
        isConnected: data.connected,
        error: data.error
      }));
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        isConnected: false,
        error: '连接检查失败'
      }));
    }
  };

  // 检查监控状态
  const checkMonitoringStatus = async () => {
    if (!selectedDeviceId) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/status/${selectedDeviceId}`);
      const data = await response.json();
      setStatus(prev => ({
        ...prev,
        isMonitoring: data.isMonitoring,
        screenshotCount: data.screenshotCount || 0,
        error: data.error
      }));
    } catch (error) {
      console.error('检查监控状态失败:', error);
    }
  };

  // 开始屏幕监控
  const startMonitoring = async () => {
    if (!selectedDevice) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: selectedDevice.id,
          deviceIp: selectedDevice.ip,
          refreshInterval
        })
      });

      if (response.ok) {
        setStatus(prev => ({
          ...prev,
          isMonitoring: true,
          error: undefined
        }));
        enqueueSnackbar('屏幕监控已启动', { variant: 'success' });
      } else {
        const errorText = await response.text();
        if (errorText.includes('该设备已在监控中') || errorText.includes('already being monitored')) {
          console.log('设备已在监控中，检查监控状态...');
          await checkMonitoringStatus();
        } else {
          setStatus(prev => ({
            ...prev,
            error: `启动监控失败: ${errorText}`
          }));
          enqueueSnackbar('启动监控失败', { variant: 'error' });
        }
      }
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        error: `启动监控失败: ${error}`
      }));
      enqueueSnackbar('启动监控失败', { variant: 'error' });
    }
  };

  // 停止屏幕监控
  const stopMonitoring = async () => {
    if (!selectedDeviceId) return;
    
    try {
      await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ deviceId: selectedDeviceId })
      });

      setStatus(prev => ({
        ...prev,
        isMonitoring: false
      }));

      if (intervalRef) {
        clearInterval(intervalRef);
        setIntervalRef(null);
      }
      enqueueSnackbar('屏幕监控已停止', { variant: 'info' });
    } catch (error) {
      console.error('停止监控失败:', error);
    }
  };

  // 刷新屏幕截图
  const refreshScreenshot = async () => {
    if (!selectedDeviceId || !status.isMonitoring || isPaused) return;

    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/screenshot/${selectedDeviceId}?t=${Date.now()}`);
      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        
        // 立即清理旧的图片URL，释放内存
        if (status.lastScreenshot) {
          URL.revokeObjectURL(status.lastScreenshot);
        }
        
        setStatus(prev => ({
          ...prev,
          lastScreenshot: imageUrl,
          screenshotCount: prev.screenshotCount + 1,
          memoryUsage: (performance as any).memory ? Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024) : undefined
        }));

        // 强制垃圾回收（如果可用）
        if ((window as any).gc) {
          setTimeout(() => (window as any).gc(), 100);
        }
      } else {
        console.error('获取截图失败:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('刷新截图失败:', error);
    }
  };

  // 下载当前截图
  const downloadScreenshot = () => {
    if (status.lastScreenshot && selectedDevice) {
      const link = document.createElement('a');
      link.href = status.lastScreenshot;
      link.download = `screenshot-${selectedDevice.name}-${new Date().toISOString().replace(/[:.]/g, '-')}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // 切换全屏
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // 清理截图文件
  const cleanupScreenshots = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SCREEN_MONITOR}/cleanup-screenshots`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        enqueueSnackbar(`截图清理完成！清理后剩余 ${result.stats.totalFiles} 张截图`, { variant: 'success' });
        
        // 更新截图计数
        setStatus(prev => ({
          ...prev,
          screenshotCount: result.stats.totalFiles
        }));
      } else {
        enqueueSnackbar('清理截图失败，请重试', { variant: 'error' });
      }
    } catch (error) {
      console.error('清理截图失败:', error);
      enqueueSnackbar('清理截图失败，请重试', { variant: 'error' });
    }
  };

  // 切换暂停状态
  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  // 设备选择变化时检查连接和监控状态
  useEffect(() => {
    if (selectedDeviceId) {
      checkConnection();
      checkMonitoringStatus();
    }
  }, [selectedDeviceId]);

  // 当设备连接成功且监控未启动时，自动启动监控
  useEffect(() => {
    if (status.isConnected && !status.isMonitoring && selectedDeviceId) {
      console.log('设备已连接，自动启动屏幕监控:', selectedDeviceId);
      startMonitoring();
    }
  }, [status.isConnected, status.isMonitoring, selectedDeviceId]);

  // 开始监控时设置定时器
  useEffect(() => {
    if (status.isMonitoring && !isPaused) {
      // 清除现有定时器
      if (intervalRef) {
        clearInterval(intervalRef);
      }
      // 设置新的定时器
      const newInterval = setInterval(refreshScreenshot, refreshInterval);
      setIntervalRef(newInterval);
    } else if (intervalRef) {
      clearInterval(intervalRef);
      setIntervalRef(null);
    }

    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
    };
  }, [status.isMonitoring, isPaused, refreshInterval, selectedDeviceId]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
      if (status.lastScreenshot) {
        URL.revokeObjectURL(status.lastScreenshot);
      }
    };
  }, []);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      {/* 顶部控制栏 */}
      <Box sx={{ p: 2, bgcolor: 'white', borderBottom: 1, borderColor: 'grey.300', flexShrink: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="h1">
            📺 {t('deviceScreenMonitor.title')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
              <IconButton onClick={toggleFullscreen}>
                {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<Close />}
              onClick={() => window.close()}
            >
              {t('common.close')}
            </Button>
          </Box>
        </Box>

        {/* 内存监控 */}
        <MemoryMonitor />

        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('deviceManager.selectDevice')}</InputLabel>
            <Select
              value={selectedDeviceId}
              onChange={(e) => setSelectedDeviceId(e.target.value)}
              disabled={devicesLoading}
            >
              {devices?.map((device) => (
                <MenuItem key={device.id} value={device.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {device.name} ({device.ip})
                    <Chip
                      label={device.status}
                      color={device.status === 'online' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedDevice && (
            <>
              <Chip
                label={status.isConnected ? '已连接' : '未连接'}
                color={status.isConnected ? 'success' : 'error'}
                size="small"
              />
              {status.isMonitoring && (
                <Chip
                  label={`监控中 (${status.screenshotCount}张)`}
                  color="info"
                  size="small"
                />
              )}
              {status.memoryUsage && (
                <Chip
                  label={`内存: ${status.memoryUsage}MB`}
                  color="warning"
                  variant="outlined"
                  size="small"
                />
              )}
            </>
          )}

          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="contained"
              startIcon={status.isMonitoring ? <Pause /> : <PlayArrow />}
              onClick={status.isMonitoring ? stopMonitoring : startMonitoring}
              disabled={!selectedDevice}
              size="small"
            >
              {status.isMonitoring ? t('deviceScreenMonitor.stopMonitoring') : t('deviceScreenMonitor.startMonitoring')}
            </Button>
            <Button
              variant="outlined"
              startIcon={isPaused ? <PlayArrow /> : <Pause />}
              onClick={togglePause}
              disabled={!status.isMonitoring}
              size="small"
            >
              {isPaused ? t('common.continue') : t('deviceScreenMonitor.pause')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={refreshScreenshot}
              disabled={!status.isMonitoring}
              size="small"
            >
              {t('deviceScreenMonitor.refresh')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={downloadScreenshot}
              disabled={!status.lastScreenshot}
              size="small"
            >
              {t('deviceScreenMonitor.downloadScreenshot')}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Delete />}
              onClick={cleanupScreenshots}
              size="small"
            >
              {t('deviceScreenMonitor.cleanScreenshots')}
            </Button>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {t('deviceScreenMonitor.refreshInterval')}:
            </Typography>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              style={{ padding: '4px 8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value={500}>{t('deviceScreenMonitor.refreshIntervalOptions.0.5s')}</option>
              <option value={1000}>{t('deviceScreenMonitor.refreshIntervalOptions.1s')}</option>
              <option value={2000}>{t('deviceScreenMonitor.refreshIntervalOptions.2s')}</option>
              <option value={3000}>{t('deviceScreenMonitor.refreshIntervalOptions.3s')}</option>
              <option value={5000}>{t('deviceScreenMonitor.refreshIntervalOptions.5s')}</option>
            </select>
          </Box>
        </Box>

        {status.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {status.error}
          </Alert>
        )}
      </Box>

      {/* 屏幕显示区域 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'black',
          position: 'relative',
          overflow: 'auto',
          p: 2
        }}
      >
        {!selectedDevice ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            color="white"
            textAlign="center"
          >
            <Typography variant="h4" gutterBottom>
              📱 {t('deviceScreenMonitor.selectDeviceToStart')}
            </Typography>
            <Typography variant="body1">
              {t('deviceScreenMonitor.selectDeviceFromAbove')}
            </Typography>
          </Box>
        ) : !status.isConnected ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            color="white"
            textAlign="center"
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              正在检查设备连接...
            </Typography>
            <Button
              variant="outlined"
              onClick={checkConnection}
              sx={{ mt: 2 }}
            >
              重新检查连接
            </Button>
          </Box>
        ) : status.isConnected && !status.isMonitoring ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            color="white"
            textAlign="center"
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('deviceScreenMonitor.deviceConnectedStartMonitoring')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrow />}
              onClick={startMonitoring}
              size="large"
            >
              开始屏幕监控
            </Button>
          </Box>
        ) : status.isMonitoring ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            {status.lastScreenshot ? (
              <img
                src={status.lastScreenshot}
                alt="设备屏幕"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  cursor: 'zoom-in'
                }}
                onClick={(e) => {
                  // 点击图片可以放大查看
                  const img = e.target as HTMLImageElement;
                  if (img) {
                    const currentTransform = img.style.transform;
                    if (currentTransform === 'scale(1.2)') {
                      img.style.transform = 'scale(1)';
                      img.style.cursor = 'zoom-in';
                    } else {
                      img.style.transform = 'scale(1.2)';
                      img.style.cursor = 'zoom-out';
                    }
                    img.style.transition = 'transform 0.3s ease';
                  }
                }}
                onDoubleClick={(e) => {
                  // 双击重置缩放
                  const img = e.target as HTMLImageElement;
                  if (img) {
                    img.style.transform = 'scale(1)';
                    img.style.cursor = 'zoom-in';
                    img.style.transition = 'transform 0.3s ease';
                  }
                }}
              />
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                color="white"
              >
                <CircularProgress color="inherit" />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  正在获取屏幕截图...
                </Typography>
              </Box>
            )}

            {isPaused && (
              <Box
                position="absolute"
                top="50%"
                left="50%"
                sx={{
                  transform: 'translate(-50%, -50%)',
                  bgcolor: 'rgba(0,0,0,0.7)',
                  color: 'white',
                  p: 2,
                  borderRadius: 2
                }}
              >
                <Typography variant="h6">{t('deviceScreenMonitor.status.paused')}</Typography>
              </Box>
            )}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default DeviceMonitor;
